import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { socketUrl } from "@/lib/api";
import { sound } from "@/lib/sound";

export function useRoomSocket(code) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [removed, setRemoved] = useState(null);
  const [offset, setOffset] = useState(0);
  const [reactions, setReactions] = useState([]);
  const wsRef = useRef(null);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    let retry;
    let ping;

    const connect = () => {
      const ws = new WebSocket(socketUrl(code));
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("open");
        ping = setInterval(() => ws.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })), 25000);
      };
      ws.onmessage = (ev) => {
        if (wsRef.current !== ws) return;
        const msg = JSON.parse(ev.data);
        if (msg.type === "state") {
          setOffset(Date.now() - msg.server_now * 1000);
          setState(msg);
        } else if (msg.type === "error" && msg.code) {
          stopRef.current = true;
          setRemoved({ reason: msg.code, message: msg.message });
        } else if (msg.type === "error") {
          toast.error(msg.message);
        } else if (msg.type === "removed") {
          stopRef.current = true;
          setRemoved({ reason: msg.reason });
        } else if (msg.type === "event") {
          toast(msg.message);
        } else if (msg.type === "reaction") {
          const r = { ...msg, key: `${msg.user_id}-${Date.now()}-${Math.random()}` };
          sound.react(msg.kind);
          setReactions((list) => [...list.slice(-11), r]);
          setTimeout(() => setReactions((list) => list.filter((x) => x.key !== r.key)), 2800);
        }
      };
      ws.onclose = () => {
        clearInterval(ping);
        if (wsRef.current !== ws) return;
        setStatus("closed");
        if (!stopRef.current) retry = setTimeout(connect, 2000);
      };
    };

    connect();
    return () => {
      stopRef.current = true;
      clearTimeout(retry);
      clearInterval(ping);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) ws.close();
    };
  }, [code]);

  const send = useCallback((type, payload = {}) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type, ...payload }));
  }, []);

  return { state, status, removed, offset, send, reactions };
}

export function useCountdown(deadline, offset) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!deadline) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, (deadline * 1000 + offset - Date.now()) / 1000));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [deadline, offset]);
  return left;
}
